import React, { useState } from 'react';
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';

const ImportEmployeeModal = ({
    show,
    onHide,
    onImport,
    canImportEmployees,
    error,
    setError
}) => {
    const [csvData, setCsvData] = useState([]);
    const [importProgress, setImportProgress] = useState(0);
    const [isImporting, setIsImporting] = useState(false);

    const downloadTemplate = () => {
        // Create sample data for employee import template
        const templateData = [
            {
                emp_code: 'EMP001',
                first_name: 'John',
                last_name: 'Doe',
                gender: 'Male',
                dob: '15/05/1990',
                doj: '15/01/2024',
                dor: '',
                email: 'john.doe@company.com',
                phone: '9876543210',
                pan_number: 'ABCDE1234F',
                aadhaar_number: '123456789012',
                ESI_NO: 'ES123456',
                PF_NO: 'PF123456',
                UAN: 'UAN123456789',
                bank_name: 'State Bank of India',
                bank_account: '1234567890',
                ifsc_code: 'SBIN0001234',
                status: 'Active'
            },
            {
                emp_code: 'EMP002',
                first_name: 'Jane',
                last_name: 'Smith',
                gender: 'Female',
                dob: '20/08/1992',
                doj: '16/01/2024',
                dor: '',
                email: 'jane.smith@company.com',
                phone: '9876543211',
                pan_number: 'FGHIJ5678K',
                aadhaar_number: '234567890123',
                ESI_NO: 'ES234567',
                PF_NO: 'PF234567',
                UAN: 'UAN234567890',
                bank_name: 'HDFC Bank',
                bank_account: '2345678901',
                ifsc_code: 'HDFC0001234',
                status: 'Active'
            },
            {
                emp_code: 'EMP003',
                first_name: 'Robert',
                last_name: 'Johnson',
                gender: 'Male',
                dob: '10/11/1988',
                doj: '17/01/2024',
                dor: '',
                email: 'robert.j@company.com',
                phone: '9876543212',
                pan_number: 'KLMNO9012P',
                aadhaar_number: '345678901234',
                ESI_NO: 'ES345678',
                PF_NO: 'PF345678',
                UAN: 'UAN345678901',
                bank_name: 'ICICI Bank',
                bank_account: '3456789012',
                ifsc_code: 'ICIC0001234',
                status: 'Inactive'
            },
            {
                emp_code: 'EMP004',
                first_name: 'Sarah',
                last_name: 'Williams',
                gender: 'Female',
                dob: '25/03/1995',
                doj: '18/01/2024',
                dor: '',
                email: 'sarah.w@company.com',
                phone: '9876543213',
                pan_number: 'PQRST5678U',
                aadhaar_number: '456789012345',
                ESI_NO: '',
                PF_NO: '',
                UAN: '',
                bank_name: 'Axis Bank',
                bank_account: '4567890123',
                ifsc_code: 'UTIB0001234',
                status: 'Active'
            },
            {
                emp_code: 'EMP005',
                first_name: 'Michael',
                last_name: 'Brown',
                gender: 'Male',
                dob: '05/07/1993',
                doj: '19/01/2024',
                dor: '20/12/2024',
                email: 'michael.b@company.com',
                phone: '9876543214',
                pan_number: 'VWXYZ1234A',
                aadhaar_number: '567890123456',
                ESI_NO: 'ES456789',
                PF_NO: 'PF456789',
                UAN: 'UAN456789012',
                bank_name: 'Kotak Mahindra Bank',
                bank_account: '5678901234',
                ifsc_code: 'KKBK0001234',
                status: 'Resigned'
            }
        ];

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(templateData);

        // Set column widths for better readability
        const wscols = [
            { wch: 10 }, // emp_code
            { wch: 12 }, // first_name
            { wch: 12 }, // last_name
            { wch: 8 },  // gender
            { wch: 12 }, // dob
            { wch: 12 }, // doj
            { wch: 12 }, // dor
            { wch: 25 }, // email
            { wch: 15 }, // phone
            { wch: 12 }, // pan_number
            { wch: 15 }, // aadhaar_number
            { wch: 10 }, // ESI_NO
            { wch: 10 }, // PF_NO
            { wch: 15 }, // UAN
            { wch: 20 }, // bank_name
            { wch: 15 }, // bank_account
            { wch: 12 }, // ifsc_code
            { wch: 10 }  // status
        ];
        ws['!cols'] = wscols;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Employees");

        // Add instructions sheet
        const instructions = [
            ['EMPLOYEE IMPORT TEMPLATE - INSTRUCTIONS'],
            [''],
            ['REQUIRED FIELDS:'],
            ['emp_code - Employee code (must be unique)'],
            ['first_name - Employee first name'],
            ['last_name - Employee last name'],
            ['email - Email address (must be unique)'],
            ['status - Active/Inactive/Resigned'],
            [''],
            ['OPTIONAL FIELDS:'],
            ['gender - Male/Female/Other'],
            ['dob - Date of birth in DD/MM/YYYY format'],
            ['doj - Date of joining in DD/MM/YYYY format'],
            ['dor - Date of resignation in DD/MM/YYYY format'],
            ['phone - Contact number'],
            ['pan_number - PAN card number'],
            ['aadhaar_number - Aadhaar number'],
            ['ESI_NO - ESI number'],
            ['PF_NO - PF number'],
            ['UAN - Universal Account Number'],
            ['bank_name - Bank name'],
            ['bank_account - Bank account number'],
            ['ifsc_code - IFSC code'],
            [''],
            ['STATUS VALUES:'],
            ['Active - Currently employed'],
            ['Inactive - On leave/suspended'],
            ['Resigned - No longer employed'],
            [''],
            ['GENDER VALUES:'],
            ['Male - Male gender'],
            ['Female - Female gender'],
            ['Other - Other gender'],
            [''],
            ['DATE FORMAT:'],
            ['• Use DD/MM/YYYY format (e.g., 15/01/2024)'],
            ['• Day: 01-31'],
            ['• Month: 01-12'],
            ['• Year: 2024, 2025, etc.'],
            ['• Leave empty if not applicable'],
            [''],
            ['IMPORTANT NOTES:'],
            ['1. Remove this instructions sheet before uploading'],
            ['2. Keep only the data sheet with header row'],
            ['3. Dates must be in DD/MM/YYYY format'],
            ['4. Employee code must be unique'],
            ['5. Email must be unique'],
            ['6. Status field is required'],
            ['7. Phone numbers should be 10 digits']
        ];

        const ws2 = XLSX.utils.aoa_to_sheet(instructions);
        const instructionCols = [
            { wch: 60 } // Wide column for instructions
        ];
        ws2['!cols'] = instructionCols;
        XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

        // Add validation sheet
        const validation = [
            ['DATA VALIDATION RULES'],
            [''],
            ['Field', 'Format', 'Required', 'Example', 'Validation'],
            ['emp_code', 'Text', 'Yes', 'EMP001', 'Unique, Alphanumeric'],
            ['first_name', 'Text', 'Yes', 'John', 'Alphabets only'],
            ['last_name', 'Text', 'Yes', 'Doe', 'Alphabets only'],
            ['gender', 'Male/Female/Other', 'No', 'Male', 'From list'],
            ['dob', 'DD/MM/YYYY', 'No', '15/05/1990', 'Date format'],
            ['doj', 'DD/MM/YYYY', 'No', '15/01/2024', 'Date format'],
            ['dor', 'DD/MM/YYYY', 'No', '', 'Date format'],
            ['email', 'Email', 'Yes', 'john@company.com', 'Valid email format'],
            ['phone', 'Numeric', 'No', '9876543210', '10 digits'],
            ['pan_number', 'Text', 'No', 'ABCDE1234F', '10 characters'],
            ['aadhaar_number', 'Numeric', 'No', '123456789012', '12 digits'],
            ['ESI_NO', 'Text', 'No', 'ES123456', 'Alphanumeric'],
            ['PF_NO', 'Text', 'No', 'PF123456', 'Alphanumeric'],
            ['UAN', 'Text', 'No', 'UAN123456789', '12 digits'],
            ['bank_name', 'Text', 'No', 'SBI', 'Bank name'],
            ['bank_account', 'Numeric', 'No', '1234567890', 'Account number'],
            ['ifsc_code', 'Text', 'No', 'SBIN0001234', '11 characters'],
            ['status', 'Active/Inactive/Resigned', 'Yes', 'Active', 'From list']
        ];

        const ws3 = XLSX.utils.aoa_to_sheet(validation);
        const validationCols = [
            { wch: 15 },
            { wch: 20 },
            { wch: 10 },
            { wch: 15 },
            { wch: 25 }
        ];
        ws3['!cols'] = validationCols;
        XLSX.utils.book_append_sheet(wb, ws3, "Validation Rules");

        // Save file
        XLSX.writeFile(wb, 'employee_import_template.xlsx');
    };

    const handleFileUpload = (event) => {
        if (!canImportEmployees()) {
            alert('You do not have permission to import employees');
            return;
        }

        const file = event.target.files[0];
        if (!file) return;

        // Check file extension
        const fileName = file.name.toLowerCase();
        const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

        if (isExcel) {
            // Handle Excel files
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });

                    // Get first sheet
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

                    // Convert to JSON with date handling
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
                        raw: false, // Get formatted values
                        dateNF: 'dd/mm/yyyy' // Date format
                    });

                    if (jsonData && jsonData.length > 0) {
                        setCsvData(jsonData);
                    } else {
                        setError('No valid data found in Excel file');
                    }
                } catch (error) {
                    setError('Error reading Excel file: ' + error.message);
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            // Handle CSV files
            Papa.parse(file, {
                header: true,
                complete: (results) => {
                    if (results.data && results.data.length > 0) {
                        setCsvData(results.data);
                    } else {
                        setError('No valid data found in CSV file');
                    }
                },
                error: (error) => {
                    setError('Error parsing CSV file: ' + error.message);
                }
            });
        }
    };

    const handleImport = async () => {
        if (!canImportEmployees()) {
            alert('You do not have permission to import employees');
            return;
        }

        if (!csvData.length) {
            setError('No data to import');
            return;
        }

        setIsImporting(true);
        setImportProgress(0);
        setError('');

        try {
            await onImport(csvData, setImportProgress);
            onHide();
            setCsvData([]);
        } catch (error) {
            setError('Import failed: ' + error.message);
        } finally {
            setIsImporting(false);
        }
    };

    const handleClose = () => {
        setCsvData([]);
        setImportProgress(0);
        setIsImporting(false);
        onHide();
    };

    if (!show) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content border-0 shadow">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title">
                            <i className="bi bi-upload me-2"></i>
                            Import Employees from CSV/Excel
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={handleClose}
                            disabled={isImporting}
                        ></button>
                    </div>

                    <div className="modal-body">
                        <div className="row g-4">
                            <div className="col-12">
                                <div className="alert alert-info">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="alert-heading">
                                                <i className="bi bi-info-circle me-2"></i>
                                                File Format Requirements
                                            </h6>
                                            <p className="mb-1">Your file should include these columns:</p>
                                            <ul className="mb-0 small">
                                                <li><strong>emp_code</strong> (required) - Unique employee code</li>
                                                <li><strong>first_name</strong> (required) - First name</li>
                                                <li><strong>last_name</strong> (required) - Last name</li>
                                                <li><strong>email</strong> (required) - Email address</li>
                                                <li><strong>status</strong> (required) - Active/Inactive/Resigned</li>
                                                <li><strong>gender</strong> - Male/Female/Other</li>
                                                <li><strong>dob</strong> - Date of birth (DD/MM/YYYY)</li>
                                                <li><strong>doj</strong> - Date of joining (DD/MM/YYYY)</li>
                                                <li><strong>dor</strong> - Date of resignation (DD/MM/YYYY)</li>
                                                <li><strong>phone</strong> - Contact number</li>
                                                <li><strong>pan_number</strong> - PAN card number</li>
                                                <li><strong>aadhaar_number</strong> - Aadhaar number</li>
                                                <li><strong>ESI_NO</strong> - ESI number</li>
                                                <li><strong>PF_NO</strong> - PF number</li>
                                                <li><strong>UAN</strong> - Universal Account Number</li>
                                                <li><strong>bank_name</strong> - Bank name</li>
                                                <li><strong>bank_account</strong> - Bank account number</li>
                                                <li><strong>ifsc_code</strong> - IFSC code</li>
                                            </ul>
                                        </div>
                                        <button
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={downloadTemplate}
                                            title="Download Excel Template"
                                        >
                                            <i className="bi bi-download me-1"></i>
                                            Template
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="card border-primary">
                                    <div className="card-header bg-primary text-white">
                                        <h6 className="mb-0">
                                            <i className="bi bi-file-earmark-excel me-2"></i>
                                            Download Template First (Important!)
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row align-items-center">
                                            <div className="col-md-8">
                                                <h6 className="text-primary">
                                                    <i className="bi bi-person-plus me-2"></i>
                                                    Use Correct Date Format
                                                </h6>
                                                <p className="mb-2">
                                                    <strong>Step 1:</strong> Download the Excel template with correct format
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Step 2:</strong> Fill your data using <span className="text-danger">DD/MM/YYYY</span> format for dates
                                                </p>
                                                <p className="mb-0 text-muted small">
                                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                                    Employee code and email must be unique
                                                </p>
                                            </div>
                                            <div className="col-md-4 text-end">
                                                <button
                                                    className="btn btn-primary btn-lg"
                                                    onClick={downloadTemplate}
                                                >
                                                    <i className="bi bi-file-earmark-excel me-2"></i>
                                                    Download Template
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">
                                            <i className="bi bi-upload me-2"></i>
                                            Upload Your File
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <label className="form-label">Select File *</label>
                                        <div className="input-group">
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept=".csv,.xlsx,.xls"
                                                onChange={handleFileUpload}
                                                disabled={isImporting}
                                            />
                                            <button
                                                className="btn btn-outline-primary"
                                                type="button"
                                                onClick={downloadTemplate}
                                                title="Download Template"
                                                disabled={isImporting}
                                            >
                                                <i className="bi bi-download me-1"></i>
                                                Template
                                            </button>
                                        </div>
                                        <div className="form-text">
                                            Supports CSV, XLSX, XLS files. Maximum file size: 10MB
                                        </div>

                                        <div className="mt-3 alert alert-warning">
                                            <i className="bi bi-calendar me-2"></i>
                                            <strong>Important:</strong> Dates must be in <span className="text-danger">DD/MM/YYYY</span> format (e.g., 15/01/2024)
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {csvData.length > 0 && (
                                <div className="col-12">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="card-title mb-0">
                                                    <i className="bi bi-table me-2"></i>
                                                    Preview ({csvData.length} records found)
                                                </h6>
                                                <span className="badge bg-primary">
                                                    <i className="bi bi-people me-1"></i>
                                                    Employee Data
                                                </span>
                                            </div>
                                            <div className="table-responsive" style={{ maxHeight: '250px' }}>
                                                <table className="table table-sm table-bordered">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            {Object.keys(csvData[0]).map(key => (
                                                                <th key={key} className="small">
                                                                    {key === 'emp_code' || key === 'first_name' || key === 'last_name' || key === 'email' || key === 'status' ? (
                                                                        <span className="text-danger">{key}*</span>
                                                                    ) : (
                                                                        key
                                                                    )}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {csvData.slice(0, 5).map((row, index) => (
                                                            <tr key={index}>
                                                                {Object.entries(row).map(([key, value], i) => (
                                                                    <td
                                                                        key={i}
                                                                        className="small"
                                                                        style={{
                                                                            color: ['emp_code', 'first_name', 'last_name', 'email', 'status'].includes(key) && !value ? 'red' : 'inherit',
                                                                            fontWeight: ['emp_code', 'first_name', 'last_name', 'email', 'status'].includes(key) ? 'bold' : 'normal'
                                                                        }}
                                                                    >
                                                                        {['emp_code', 'first_name', 'last_name', 'email', 'status'].includes(key) && !value ? (
                                                                            <span className="text-danger">Required!</span>
                                                                        ) : (
                                                                            value || ''
                                                                        )}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {csvData.length > 5 && (
                                                <div className="text-center small text-muted mt-2">
                                                    <i className="bi bi-arrow-down"></i>
                                                    ... and {csvData.length - 5} more records
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isImporting && (
                                <div className="col-12">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6 className="card-title">
                                                <i className="bi bi-arrow-repeat me-2"></i>
                                                Importing Employees...
                                            </h6>
                                            <div className="progress mb-2">
                                                <div
                                                    className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                                    style={{ width: `${importProgress}%` }}
                                                >
                                                    {importProgress}%
                                                </div>
                                            </div>
                                            <small className="text-muted">
                                                Processing employee records. Please wait...
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleClose}
                            disabled={isImporting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={downloadTemplate}
                            disabled={isImporting}
                        >
                            <i className="bi bi-download me-2"></i>
                            Get Template
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleImport}
                            disabled={!csvData.length || isImporting}
                        >
                            {isImporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-upload me-2"></i>
                                    Start Import
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportEmployeeModal;